import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { companyCheckout, getCompanyInfo, getPublicPricingPlans } from '../services/api';
import './Company.css';
import './Checkout.css';

const truthyFlag = (value) => value === true || value === 'true' || value === 1 || value === '1';

const normalizeKey = (key) =>
  String(key || '')
    .toLowerCase()
    .replace('premium', 'pro')
    .trim();

const Checkout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, isCompanyAdmin, refreshUser } = useAuth();

  const companyAdmin = useMemo(
    () =>
      truthyFlag(isCompanyAdmin) ||
      truthyFlag(user?.is_company_admin) ||
      truthyFlag(localStorage.getItem('isCompanyAdmin')),
    [isCompanyAdmin, user]
  );

  const planParam = normalizeKey(params.get('plan'));

  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState('');
  const [plan, setPlan] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [result, setResult] = useState(null);

  const [form, setForm] = useState({
    company: '',
    email: '',
    cardName: '',
    cardNumber: '4242 4242 4242 4242',
    expiry: '12/28',
    cvc: '123',
  });

  useEffect(() => {
    if (!companyAdmin || !planParam) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const [pricing, companyInfo] = await Promise.all([
          getPublicPricingPlans().catch(() => []),
          getCompanyInfo().catch(() => null),
        ]);
        if (cancelled) return;
        const name = companyInfo?.company?.name || user?.company_name || '';
        setCompanyName(name);
        const match = (Array.isArray(pricing) ? pricing : []).find(
          (p) => normalizeKey(p.plan_key) === planParam
        );
        if (!match) {
          setError(t('checkout.planNotFound', 'Plan not found.'));
          setPlan(null);
        } else {
          setPlan(match);
        }
        setForm((prev) => ({
          ...prev,
          company: name,
          email: user?.email || '',
          cardName: user?.username || name || 'Card Holder',
        }));
      } catch (e) {
        if (!cancelled) setError(e.message || t('checkout.loadFailed', 'Failed to load checkout'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyAdmin, planParam, t, user?.company_name, user?.email, user?.username]);

  if (!companyAdmin) {
    return <Navigate to="/dashboard/upgrade" replace />;
  }

  if (!planParam) {
    return <Navigate to="/dashboard/upgrade" replace />;
  }

  const priceLabel =
    plan && Number(plan.price_monthly) > 0
      ? `${plan.price_monthly} ${plan.currency || 'TRY'}`
      : t('checkout.customPrice', 'Custom');

  const onPay = async (e) => {
    e.preventDefault();
    if (!plan || paying) return;
    setPaying(true);
    setError('');
    try {
      const data = await companyCheckout(plan.plan_key || planParam);
      setResult(data);
      if (typeof refreshUser === 'function') {
        await refreshUser().catch(() => null);
      }
    } catch (err) {
      setError(err.message || t('checkout.paymentFailed', 'Payment failed.'));
    } finally {
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <div className="co-page ck-page">
        <div className="co-empty">
          <div className="co-spinner" />
          <p>{t('checkout.loading', 'Preparing payment…')}</p>
        </div>
      </div>
    );
  }

  if (result?.invoice) {
    const inv = result.invoice;
    const lic = result.license || {};
    return (
      <div className="co-page ck-page">
        <section className="ck-success" aria-live="polite">
          <div className="ck-success-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 6L9 17l-5-5" />
            </svg>
          </div>
          <p className="ck-kicker">{t('checkout.paidKicker', 'Payment complete')}</p>
          <h1>{t('checkout.paidTitle', 'You’re upgraded')}</h1>
          <p className="ck-lead">
            {t('checkout.paidLead', {
              plan: lic.display_name || plan?.display_name || planParam,
              defaultValue: '{{plan}} is now active. (Demo payment)',
            })}
          </p>

          <div className="ck-invoice">
            <div className="ck-invoice-head">
              <strong>{inv.number || `INV-${inv.id}`}</strong>
              <span className="ck-paid-pill">{t('checkout.paid', 'Paid')}</span>
            </div>
            <dl>
              <div>
                <dt>{t('checkout.company', 'Company')}</dt>
                <dd>{result.company?.name || companyName || '—'}</dd>
              </div>
              <div>
                <dt>{t('checkout.plan', 'Plan')}</dt>
                <dd>{lic.display_name || plan?.display_name}</dd>
              </div>
              <div>
                <dt>{t('checkout.amount', 'Amount')}</dt>
                <dd>
                  {inv.amount} {inv.currency}
                </dd>
              </div>
              <div>
                <dt>{t('checkout.status', 'Status')}</dt>
                <dd>{inv.status}</dd>
              </div>
              <div>
                <dt>{t('checkout.paidAt', 'Paid at')}</dt>
                <dd>{inv.paid_at ? new Date(inv.paid_at).toLocaleString() : '—'}</dd>
              </div>
            </dl>
          </div>

          <div className="ck-actions">
            <Link className="co-btn primary" to="/dashboard/company">
              {t('checkout.toBilling', 'View company billing')}
            </Link>
            <Link className="co-btn ghost" to="/dashboard">
              {t('checkout.toDashboard', 'Back to dashboard')}
            </Link>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="co-page ck-page">
      <section className="ck-hero">
        <button type="button" className="ck-back" onClick={() => navigate('/dashboard/upgrade')}>
          ← {t('checkout.back', 'Back to plans')}
        </button>
        <p className="ck-kicker">{t('checkout.kicker', 'Secure checkout')}</p>
        <h1>{t('checkout.title', 'Complete payment')}</h1>
        <p className="ck-lead">
          {t(
            'checkout.demoLead',
            'This is a demo payment screen. Card details are not processed; Pay upgrades your plan and creates an invoice.'
          )}
        </p>
      </section>

      <div className="ck-grid">
        <form className="ck-card" onSubmit={onPay}>
          <h2>{t('checkout.paymentDetails', 'Payment details')}</h2>

          <label>
            <span>{t('checkout.company', 'Company')}</span>
            <input
              value={form.company}
              onChange={(e) => setForm({ ...form, company: e.target.value })}
              autoComplete="organization"
            />
          </label>

          <label>
            <span>{t('checkout.email', 'Billing email')}</span>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              autoComplete="email"
              required
            />
          </label>

          <label>
            <span>{t('checkout.cardName', 'Name on card')}</span>
            <input
              value={form.cardName}
              onChange={(e) => setForm({ ...form, cardName: e.target.value })}
              autoComplete="cc-name"
              required
            />
          </label>

          <label>
            <span>{t('checkout.cardNumber', 'Card number')}</span>
            <input
              value={form.cardNumber}
              onChange={(e) => setForm({ ...form, cardNumber: e.target.value })}
              inputMode="numeric"
              autoComplete="cc-number"
              required
            />
          </label>

          <div className="ck-row">
            <label>
              <span>{t('checkout.expiry', 'Expiry')}</span>
              <input
                value={form.expiry}
                onChange={(e) => setForm({ ...form, expiry: e.target.value })}
                autoComplete="cc-exp"
                required
              />
            </label>
            <label>
              <span>CVC</span>
              <input
                value={form.cvc}
                onChange={(e) => setForm({ ...form, cvc: e.target.value })}
                autoComplete="cc-csc"
                required
              />
            </label>
          </div>

          {error ? <p className="ck-error">{error}</p> : null}

          <button type="submit" className="co-btn primary ck-pay" disabled={paying || !plan}>
            {paying
              ? t('checkout.processing', 'Processing…')
              : `${t('checkout.pay', 'Pay')} ${priceLabel}/mo`}
          </button>
          <p className="ck-demo-note">{t('checkout.demoNote', 'Demo · No real charge')}</p>
        </form>

        <aside className="ck-summary">
          <h2>{t('checkout.summary', 'Order summary')}</h2>
          {plan ? (
            <>
              <div className="ck-summary-plan">
                <strong>{plan.display_name}</strong>
                <span>
                  {Number(plan.price_monthly) > 0
                    ? `${plan.price_monthly} ${plan.currency || 'TRY'}/mo`
                    : t('company.plan.contact', 'Contact us')}
                </span>
              </div>
              <ul>
                <li>
                  {plan.max_users > 0
                    ? `${plan.max_users} ${t('company.plan.users', 'users')}`
                    : t('company.plan.unlimitedUsers', 'Unlimited users')}
                </li>
                <li>
                  {plan.max_connections > 0
                    ? `${plan.max_connections} ${t('company.plan.connections', 'connections')}`
                    : t('company.plan.unlimitedConnections', 'Unlimited connections')}
                </li>
              </ul>
              <div className="ck-total">
                <span>{t('checkout.totalDue', 'Total due today')}</span>
                <strong>
                  {Number(plan.price_monthly) > 0
                    ? `${plan.price_monthly} ${plan.currency || 'TRY'}`
                    : '0'}
                </strong>
              </div>
            </>
          ) : (
            <p className="ck-error">{error || t('checkout.loadPlanError', 'Could not load plan.')}</p>
          )}
        </aside>
      </div>
    </div>
  );
};

export default Checkout;
